import { useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Banner } from 'components/banner';
import { AlertCircle } from 'components/icons/alert-circle';
import type {
  BehaviorCode,
  HorizonCode,
  InterestCode,
  LossCode,
  ObjectiveCode,
  SuitabilityAnswers,
} from './answers';
import {
  RadioQuestion,
  type RadioOption,
} from 'src/suitability/engine/components/radio-question';
import {
  getCoherenceErrors,
  getIncompatibleP10Options,
  getIncompatibleP9Options,
} from './coherence';
import { useWioCapitalContent } from './content';

interface StepProps {
  answers: SuitabilityAnswers;
  onUpdate: (patch: Partial<SuitabilityAnswers>) => void;
}

export const StepBlock2 = ({ answers, onUpdate }: StepProps) => {
  const content = useWioCapitalContent();
  const step = content.step('block2');

  const { t } = useTranslation();
  const errors = getCoherenceErrors(answers);
  const incompatibleP9 = getIncompatibleP9Options(answers.p7);
  const incompatibleP10 = getIncompatibleP10Options(answers.p7);
  const isElderly = answers.p11 === 'Over75';

  // Si cambia P7 y la selección de P9/P10 queda incompatible, la limpiamos.
  useEffect(() => {
    if (answers.p9 && incompatibleP9.includes(answers.p9)) {
      onUpdate({ p9: undefined });
    }
    if (answers.p10 && incompatibleP10.includes(answers.p10)) {
      onUpdate({ p10: undefined });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers.p7]);

  const horizonOptions: RadioOption[] = content.radio('p6').options.map((option) => ({
    ...option,
    disabled: isElderly && option.value === 'Long',
    disabledReason:
      isElderly && option.value === 'Long'
        ? t('wioCapital.disabled.elderlyHorizon')
        : undefined,
  }));

  const lossOptions: RadioOption[] = content.radio('p9').options.map((option) => ({
    ...option,
    disabled: incompatibleP9.includes(option.value),
    disabledReason: incompatibleP9.includes(option.value)
      ? t('wioCapital.disabled.incoherentObjective')
      : undefined,
  }));

  const behaviorOptions: RadioOption[] = content.radio('p10').options.map((option) => ({
    ...option,
    disabled: incompatibleP10.includes(option.value),
    disabledReason: incompatibleP10.includes(option.value)
      ? t('wioCapital.disabled.incoherentObjective')
      : undefined,
  }));

  return (
    <Box>
      <Typography variant="textXl" sx={{ fontWeight: 700, mb: 1 }}>
        {step.title}
      </Typography>
      <Typography
        variant="textSm"
        sx={{ color: 'text.secondary', mb: 3, display: 'block' }}
      >
        {step.intro}
      </Typography>

      <RadioQuestion
        question={content.radio('p6').prompt}
        options={horizonOptions}
        value={answers.p6}
        onChange={(v) => onUpdate({ p6: v as HorizonCode })}
        required
      />
      <RadioQuestion
        question={content.radio('p7').prompt}
        options={content.radio('p7').options}
        value={answers.p7}
        onChange={(v) => onUpdate({ p7: v as ObjectiveCode })}
        required
      />
      <RadioQuestion
        question={content.radio('p8').prompt}
        options={content.radio('p8').options}
        value={answers.p8}
        onChange={(v) => onUpdate({ p8: v as InterestCode })}
        required
      />
      <RadioQuestion
        question={content.radio('p9').prompt}
        options={lossOptions}
        value={answers.p9}
        onChange={(v) => onUpdate({ p9: v as LossCode })}
        required
      />
      <RadioQuestion
        question={content.radio('p10').prompt}
        options={behaviorOptions}
        value={answers.p10}
        onChange={(v) => onUpdate({ p10: v as BehaviorCode })}
        required
      />

      {errors.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Banner
            color="orange"
            Icon={<AlertCircle />}
            message={errors.map((coherenceError) => (
              <Box
                key={coherenceError.id}
                component="span"
                sx={{ display: 'block', mb: 0.5 }}
              >
                {t(coherenceError.messageKey)}
              </Box>
            ))}
          />
        </Box>
      )}
    </Box>
  );
};
